import type {
  Clazz,
  Ctor,
  FunctionType,
  KeysOfOptions,
  Objectish,
  RecordKey,
} from '../identity.ts';
import {
  getPrototypeChainOf,
  isArray,
  isFunction,
  isIterable,
  isObject,
  isPresent,
  keysOf,
} from '../identity.ts';
import { asNumber } from '../math_core.ts';
import { IsBrowser, IsNode, IsProduction } from './env.ts';
import { buildStyle, Style, type LogStyle, type StyleData } from './log_style.ts';

export type LogEntry = [string, ...unknown[]];
type LogOutputFormat = 'css' | 'ansi' | 'none';

let defaultOutputFormat: LogOutputFormat = IsBrowser
  ? 'css'
  : IsNode && !IsProduction
    ? 'ansi'
    : 'none';

export function setDefaultLogOutputFormat(format: LogOutputFormat) {
  defaultOutputFormat = format;
}

const indentation = '                    ';

export class LogEntryBuilder {
  private readonly formats_: string[] = [];
  private readonly values_: unknown[] = [];
  private readonly outputFormat_: LogOutputFormat;
  private currentStyle_: StyleData | undefined;
  private pendingStyle_ = false;
  private readonly indentSize_;
  private indentDepth_ = 0;

  constructor(options?: {
    style?: LogStyle | StyleData | undefined;
    format?: LogOutputFormat | undefined;
    indent?: number | undefined;
  }) {
    this.outputFormat_ = options?.format ?? defaultOutputFormat;
    this.indentSize_ = options?.indent ?? 0;
    if (options?.style) {
      this.style(options.style);
    }
  }

  applyStyle_() {
    if (!this.pendingStyle_) {
      return;
    }
    this.pendingStyle_ = false;
    if (!this.currentStyle_) {
      return;
    }
    if (this.outputFormat_ === 'css') {
      this.formats_.push('%c');
      this.values_.push(this.currentStyle_.css);
    } else if (this.outputFormat_ === 'ansi') {
      this.formats_.push(this.currentStyle_.ansi);
    }
  }

  indent(increase: boolean) {
    this.indentDepth_ += increase ? 1 : -1;
  }

  break(newline = true) {
    if (this.indentSize_) {
      this.formats_.push(
        `${newline ? '\n' : ''}${indentation.slice(0, this.indentDepth_ * this.indentSize_)}`,
      );
    }
  }

  add(format: string, ...values: unknown[]) {
    this.applyStyle_();
    this.formats_.push(format);
    this.values_.push(...values);
    return this;
  }

  // Log as if sent to console.log.
  log(...values: unknown[]) {
    const previousStyle = this.currentStyle_;
    // TODO: Look into setting max depth/breadth on the builder.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    values.forEach((v) => formatValue(this, v));
    // TODO: Don't "pop" until the next command, in case it's another override?
    if (previousStyle) {
      this.style(previousStyle);
    } else if (this.outputFormat_ === 'ansi') {
      // TODO: How to handle css in this case?
      this.style(Style.default);
    } else {
      this.currentStyle_ = undefined;
      this.pendingStyle_ = false;
    }
    return this;
  }

  clearToEndOfLine() {
    // Can we infer this from a line ending with background color set?
    if (this.outputFormat_ === 'ansi') {
      this.formats_.push('\x1b[K');
    }
  }

  style(style: LogStyle | StyleData, additive = false) {
    if (additive && this.currentStyle_) {
      if ('css' in style) {
        this.currentStyle_ = buildStyle(this.currentStyle_.style, (style as StyleData).style);
      } else {
        this.currentStyle_ = buildStyle(this.currentStyle_.style, style);
      }
    } else {
      if ('css' in style) {
        this.currentStyle_ = style as StyleData;
      } else {
        this.currentStyle_ = buildStyle(style);
      }
    }
    this.pendingStyle_ = true;

    return this;
  }

  setStyled(style: LogStyle | StyleData, format?: string, ...values: unknown[]) {
    this.style(style);
    if (isPresent(format)) {
      this.add(format, ...values);
    }
    return this;
  }

  override(style: LogStyle | StyleData, format: string, ...values: unknown[]) {
    const previousStyle = this.currentStyle_;
    this.style(style, true);
    this.add(format, ...values);
    // TODO: Don't "pop" until the next command, in case it's another override?
    if (previousStyle) {
      this.style(previousStyle);
    } else if (this.outputFormat_ === 'ansi') {
      // TODO: How to handle css in this case?
      this.style(Style.default);
    } else {
      this.currentStyle_ = undefined;
      this.pendingStyle_ = false;
    }
    return this;
  }

  /** @deprecated */
  addStyled(style: LogStyle | StyleData, format?: string, ...values: unknown[]) {
    return this.setStyled(style, format, ...values);
  }

  // If the builder is non-empty, the separator is appended after resetting the style.
  reset(separator?: string, forceSeparator?: boolean) {
    if (this.currentStyle_) {
      this.currentStyle_ = undefined;
      this.pendingStyle_ = false;
      if (this.outputFormat_ === 'ansi') {
        this.formats_.push(Style.default.ansi);
      }
    }
    if (isPresent(separator) && (this.formats_.length || forceSeparator === true)) {
      this.formats_.push(separator);
    }
    return this;
  }

  clear() {
    this.formats_.length = 0;
    this.values_.length = 0;
  }

  build(clear?: boolean, separator?: string): LogEntry {
    this.reset(separator);
    const result: LogEntry = [this.formats_.join(''), ...this.values_];
    if (clear === true) {
      this.clear();
    }
    return result;
  }
}

export type LoggableOptions<T, O> = O & {
  format: (b: LogEntryBuilder, v: T, o: O) => void;
  stringify?: (v: T) => string;
};

export class Loggable<T, O> {
  constructor(
    readonly value: T,
    private readonly options: LoggableOptions<T, O>,
  ) {}

  format(builder: LogEntryBuilder) {
    this.options.format(builder, this.value, this.options);
  }

  toString() {
    if (this.options.stringify) {
      return this.options.stringify(this.value);
    }
    const builder = new LogEntryBuilder({ format: 'none' });
    this.format(builder);
    return builder.build()[0];
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function logTypeName(value: any): string {
  return isObject(value) ? value.constructor.name : typeof value;
}

// https://stackoverflow.com/a/31564520/8107589
export function logPrototypeChainOf(value: object): string {
  return getPrototypeChainOf(value)
    .map((o) => o.constructor.name)
    .join(' --> ');
}

function valueId(options: { delims: string; index?: number; label?: string; ref: boolean }) {
  let result = `${options.delims[0]} /*`;
  if (options.index !== undefined) {
    result += ` ${options.ref ? '@' : '#'}${options.index}`;
  }
  if (options.label !== undefined) {
    result += ` ${options.label}`;
  }
  result += ` */`;
  if (options.ref) {
    result += ` ${options.delims[1]}`;
  }
  return result;
}

const ignoredObjects = new Array<[unknown, string]>();

export function ignoreObjectInLog(obj: unknown, label: string) {
  ignoredObjects.push([obj, valueId({ delims: '{}', label, ref: true })]);
}

const elidedSuperclasses: Array<Ctor> = [EventTarget];

export function elideSuperclassInLog(...ctors: Array<Ctor>) {
  elidedSuperclasses.push(...ctors);
}

export enum LogValue {
  Stringify,
  Custom,
}

export interface ILogValueOptions extends KeysOfOptions {
  maxDepth?: number;
  maxArrayElements?: number;
  maxTotalLength?: number;
  impl?: LogValue;
  indent?: string;
  showSymbolKeys?: boolean;
  showUndefinedProperties?: boolean;
  expandIterable?: boolean;
}

// TODO(bpm): Make max keys shrink as depth increases?
const defaultMaxEntries = 50; // TODO(bpm): Maybe 25 if we can get rid of obviously uninteresting keys?

// TODO(bpm): Consider taking advantage of enhanced console logging instead or in addition.
// https://developer.chrome.com/docs/devtools/console/api/
export function logValue(
  value: unknown,
  options: ILogValueOptions = {},
  seenObjects?: Map<unknown, string>,
): string {
  // Note: We could do something complex with the replacer argument to stringify, if we don't
  // like the results of JSON.prune.
  let result: string;
  if (options.impl === LogValue.Stringify) {
    result = JSON.stringify(value, undefined, 2);
  } else {
    if (value === undefined) {
      result = 'undefined';
    } else if (value === null) {
      result = 'null';
    } else if (typeof value === 'function') {
      result = 'function';
    } else if (typeof value === 'string') {
      result = `"${value}"`;
    } else if (typeof value === 'symbol') {
      result = String(value);
    } else if (!(options.expandIterable ?? false) && isIterable(value)) {
      // This might be a generator or other single-use iterable, so we can't iterate over it.
      result = '<iterable>';
    } else if (typeof value !== 'object') {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string
      result = `${value}`;
    } else {
      const valueAsArray = isArray(value)
        ? value
        : isIterable(value)
          ? Array.from(value)
          : undefined;
      const delims = isPresent(valueAsArray) ? '[]' : '{}';
      seenObjects ??= new Map(ignoredObjects);
      const index = seenObjects.size - ignoredObjects.length;
      const label = value.constructor.name;
      result = valueId({ delims, index, label, ref: false });
      seenObjects.set(value, valueId({ delims, index, label, ref: true }));

      const indent = options.indent ?? '';
      const subIndent = indent + '  ';

      let enumerable: readonly unknown[];
      if (valueAsArray) {
        enumerable = valueAsArray;
      } else if (elidedSuperclasses.some((sc) => value instanceof sc)) {
        enumerable = [];
      } else {
        enumerable = keysOf(value, options);
      }
      let emittedEntries = 0;
      for (const e of enumerable) {
        let logv: string | undefined;
        if (!valueAsArray && typeof e === 'symbol' && !(options.showSymbolKeys ?? false)) {
          continue;
        }
        const v = valueAsArray ? e : (value as Record<RecordKey, unknown>)[e as RecordKey];
        logv = seenObjects.get(v);
        if (logv === undefined) {
          // console.log(`${e} ${logTypeName(value)}`);
          logv = logValue(
            v,
            {
              ...options,
              indent: subIndent,
            },
            seenObjects,
          );
          if (valueAsArray) {
            // Skip property checks
          } else if (logv === 'undefined' && !(options.showUndefinedProperties ?? false)) {
            continue;
          }
        }
        if (++emittedEntries > defaultMaxEntries) {
          result += `\n${subIndent}…`;
          break;
        } else if (valueAsArray) {
          result += `\n${subIndent}${logv}`;
        } else {
          result += `\n${subIndent}${String(e)}: ${logv}`;
        }
      }
      if (emittedEntries === 0) {
        result += ` ${delims[1]}`;
      } else {
        result += `\n${indent}${delims[1]}`;
      }
    }
  }
  // TODO(bpm): Make this 1024;
  const maxTotalLength = options.maxTotalLength ?? 10024;
  if (result.length <= maxTotalLength) {
    return result;
  }
  if (result[0] === '{') {
    return `${result.substring(0, maxTotalLength - 2)}…}`;
  }
  return `${result.substring(0, maxTotalLength - 1)}…`;
}

export function logNumber(v: number, decimals = 2, keepTrailingZeroes = false) {
  // https://stackoverflow.com/a/19623253/8107589
  const fixed = v.toFixed(decimals);
  return keepTrailingZeroes ? fixed : Number(fixed).toString();
}

const radixPrefix = {
  2: '0b',
  16: '0x',
} as const;

function formatRadix(
  b: LogEntryBuilder,
  v: number,
  options: {
    base: 2 | 16;
    minDigits?: number | undefined;
  },
): LogEntry {
  // https://stackoverflow.com/a/16155417/8107589
  let s = (v >>> 0).toString(options.base);
  if (options.minDigits !== undefined) {
    s = s.padStart(options.minDigits, '0');
  }
  return b.override(Style.subtle, radixPrefix[options.base]).override(Style.number, s).build();
}

export function logBinary(v: number, minDigits?: number) {
  return new Loggable(v, { base: 2, minDigits, format: formatRadix });
}

export function logHex(v: number, minDigits?: number) {
  return new Loggable(v, { base: 16, minDigits, format: formatRadix });
}

enum LogEvent {
  Call,
  Return,
  Both,
}

let logIndent = '';

// function logMethod<T, P extends [], R>(
//   prefix: string,
//   method: (this: T, ...args: P) => R,
function logMethod<F extends FunctionType>(
  prefix: string,
  method: F,
  className: string,
  methodName: string | symbol,
  event: LogEvent,
) {
  return function thunk(this: ThisType<F>, ...args: Parameters<F>): ReturnType<F> {
    if (event !== LogEvent.Return) {
      console.log(
        `${logIndent}> ${prefix} ${className}.${methodName.toString()}()`,
        ...(args as unknown[]),
      );
      // TODO: Log names and types of arguments
      // console.log(`${prefix + methodName.toString()}(`);
      // args.forEach((arg) => {

      // })
      // console.log(')');
    }
    const oldIndent = logIndent;
    logIndent += '  ';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = method.apply(this, args);
    logIndent = oldIndent;
    if (event !== LogEvent.Call) {
      console.log(
        `${logIndent}< ${prefix} ${className}.${methodName.toString()}:`,
        logTypeName(result),
        '=',
        result,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return result;
  };
}

// Decorate methods to log entry and exit along with arguments and returns types.
export function logMethodCall(label?: string): MethodDecorator {
  const prefix = label !== undefined ? `${label}:` : '';
  return <T>(
    target: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>,
  ): TypedPropertyDescriptor<T> => {
    const className = target.constructor.name;
    const d = descriptor;
    // const d = descriptor ?? Object.getOwnPropertyDescriptor(target, propertyKey);
    // if (d) {
    // TODO: How is this supposed to work?
    // if (d.value !== undefined) {
    //   d.value = logMethod(prefix, d.value, className, propertyKey, LogEvent.Both);
    // }
    if (d.get) {
      d.get = logMethod(prefix, d.get, className, propertyKey, LogEvent.Return);
    }
    if (d.set) {
      d.set = logMethod(prefix, d.set, className, propertyKey, LogEvent.Call);
    }
    // }
    return d;
  };
}

// Logs the intermediate values of an observable chain.
//   obs.map(logMap('prefix')).foo()
export function logMap<T>(label?: string): (value: T) => T {
  const prefix = label !== undefined ? `${label} ->` : '->';
  return (value): T => {
    console.log(prefix, value);
    return value;
  };
}

function appendPath(path: string, value: unknown, prop: string): string {
  if (isArray(value)) {
    return `${path}[${prop}]`;
  }
  return path ? `${path}.${prop}` : prop;
}

function logEqualsInternal<T>(x: T, y: T, prefix: string, path: string): boolean {
  if (x === y) {
    return true;
  }
  if (typeof x !== typeof y) {
    console.log(prefix, `${path} types: ${typeof x} !== ${typeof y}`);
    return false;
  }
  if (!isObject(x) || !isObject(y)) {
    console.log(prefix, `${path} values: ${JSON.stringify(x)} !== ${JSON.stringify(y)}`);
    return false;
  }
  if (x.constructor !== y.constructor) {
    console.log(prefix, `${path} types: ${x.constructor.name} !== ${y.constructor.name}`);
    return false;
  }
  return (
    Object.keys(x).every((k) => {
      const field = appendPath(path, x, k);
      if (!(k in y)) {
        console.log(prefix, `${field} absent from rhs`);
        return false;
      }
      if (!logEqualsInternal(x[k], y[k], prefix, field)) {
        return false;
      }
      return true;
    }) &&
    Object.keys(y).every((k) => {
      if (!(k in x)) {
        const field = appendPath(path, y, k);
        console.log(prefix, `${field} absent from lhs`);
        return false;
      }
      return true;
    })
  );
}

// Equivalent to equals(), except that differences are logged.
export function logEquals<T>(label?: string, logWhenEqual = false): (x: T, y: T) => boolean {
  const prefix = label !== undefined ? `${label}:` : '';
  return (a, b): boolean => {
    const result = logEqualsInternal(a, b, prefix, '');
    if (result && logWhenEqual) {
      console.log(prefix, 'equal');
    }
    return result;
  };
}

const stackFrameRE = /^ *(at +)?(?<frame>.*[^ ]+) *$/;

export function extractStackFromError(err: Error, skipDepth = 0) {
  if (err.stack === undefined) {
    return [];
  }
  // Increase skipDepth by 1 to skip over error message.
  let frames = err.stack.split('\n').slice(skipDepth + 1);
  frames = frames.map((f) => {
    const match = f.match(stackFrameRE);
    return match?.groups?.frame ?? f;
  });
  return frames;
}

// This doesn't seem to add enough value to be worthwhile, and TypeScript can't infer the else
// clause anyway.
// export function getCallStack() {
//   if ('captureStackTrace' in Error) {
//     // Avoid getStack itself in the stack trace
//     const obj: { stack?: string } = {};
//     Error.captureStackTrace(obj, getCallStack);
//     return obj.stack;
//   } else {
//     const error = new Error('');
//     return error.stack;
//   }
// }

const funcRE = '((?<func>[^ ]+) +)?[^(]*\\(?';
const urlPrefixRE = '(https?://[^/]+/(@fs/)?)?';
const pathRE = '(?<path>([A-Z]:)?[^:]*/)';
const fileRE = '(?<file>[^/?:)]+)';
const urlQueryRE = '(\\?[^:)]*)?';
const lineRE = '(:(?<line>\\d+))?(:(?<column>\\d+))?';
const stackFunctionRE = new RegExp(
  `^${funcRE}${urlPrefixRE}${pathRE}${fileRE}${urlQueryRE}${lineRE}\\)?$`,
);

export function extractCallSiteFromFrame(frame: string) {
  const match = frame.match(stackFunctionRE);
  if (!match?.groups) {
    return undefined;
  }
  let { func, file, path, line, column } = match.groups;
  return { func, file, path, line: asNumber(line), column: asNumber(column) };
}

export function extractCallSiteFromError(err: Error, skipDepth = 0, debug = false) {
  const stack = extractStackFromError(err, skipDepth);
  if (stack.length === 0) {
    return undefined;
  }
  if (debug) {
    console.log(`|extractCallSiteFromError(${err.stack}, ${stack[0]})`);
  }
  return extractCallSiteFromFrame(stack[0]);
}

export function logCallSite(skipDepth = 0) {
  return extractCallSiteFromError(new Error(), skipDepth + 1);
}

type FormatResult =
  | {
      result: 'builder';
    }
  | {
      result: 'record';
      record: Record<string, unknown>;
    }
  | {
      result: 'unknown';
    };

type Formatter<T> = (value: T, builder: LogEntryBuilder) => FormatResult;

class FormatMap {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-function-type
  private map = new Map<Function, Formatter<any>>();

  set<T extends object>(ctor: Clazz<T>, formatter: Formatter<T>): this {
    this.map.set(ctor, formatter);
    return this;
  }

  format(builder: LogEntryBuilder, value: object): FormatResult {
    if (value instanceof Loggable) {
      value.format(builder);
      return { result: 'builder' };
    }
    const formatter = this.map.get(value.constructor);
    return formatter?.(value, builder) ?? { result: 'unknown' };
  }
}

const objectFormatters = new FormatMap();

export function registerObjectFormatter<T extends object>(ctor: Ctor<T>, formatter: Formatter<T>) {
  objectFormatters.set(ctor, formatter);
}

// This allows registering classes with non-public constructors, but the type information is weaker.
export function registerObjectClassFormatter<T extends object>(
  clazz: Clazz<T>,
  formatter: Formatter<T>,
) {
  objectFormatters.set(clazz, formatter);
}

registerObjectFormatter(Set, (v) => ({
  result: 'record',
  record: { type: 'Set', values: Array.from(v.values()) } as const,
}));
registerObjectFormatter(Map, (v) => ({
  result: 'record',
  record: { type: 'Map', entries: Object.fromEntries(v.entries()) },
}));

export function maybeFormatRegisteredObject(value: {}): string | undefined {
  const builder = new LogEntryBuilder({ format: 'none' });
  const format = objectFormatters.format(builder, value);
  return format.result === 'builder' ? builder.build()[0] : undefined;
}

// TODO: Test-only export
export function formatValue<T>(builder: LogEntryBuilder, value: T, maxDepth = 0, maxBreadth = 5) {
  if (isArray(value)) {
    if (maxDepth > 0 || value.length === 0) {
      builder.override(Style.subtle, '[');
      builder.indent(true);
      let first = true;
      for (const v of value) {
        if (first) {
          first = false;
        } else {
          builder.override(Style.subtle, ', ');
        }
        // TODO: Return the length in formatValue and implement maxBreadth in terms of characters.
        if (maxBreadth <= 0) {
          builder.override(Style.subtle, '…');
          break;
        }
        builder.break();
        formatValue(builder, v, maxDepth - 1, maxBreadth);
        maxBreadth -= 1;
      }
      builder.indent(false);
      builder.override(Style.subtle, ']');
    } else {
      // Ideally, we'd fall through to '%o', but that interacts poorly with css styles in vscode.
      // TODO: Emit count of elements.
      builder.override(Style.subtle, '[…]');
    }
  } else if (isObject(value)) {
    let objectish: Objectish;
    // TODO: We should restore the format style after this call.
    const format = objectFormatters.format(builder, value);
    switch (format.result) {
      case 'builder':
        return;
      case 'record':
        objectish = format.record;
        break;
      case 'unknown':
        if (isIterable(value)) {
          builder.override(Style.keyword, '<iterable>');
          return;
        }
        objectish = value;
        break;
    }
    // eslint-disable-next-line @typescript-eslint/unbound-method
    if (isPresent(objectish.toString) && objectish.toString !== Object.prototype.toString) {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      builder.override(Style.string, objectish.toString());
      return;
    }
    if (isPresent(objectish.constructor) && objectish.constructor !== Object) {
      builder.override(Style.type, objectish.constructor.name);
    }
    const keys = keysOf(objectish, { prioritizedKeys: ['id', 'name'] as const });
    if (maxDepth > 0 || keys.length === 0) {
      let first = true;
      builder.override(Style.subtle, '{');
      builder.indent(true);
      for (const k of keys) {
        const v = objectish[k];
        // if (isFunction(v)) {
        //   continue;
        // }
        // TODO: Return the length in formatValue and implement maxBreadth in terms of characters.
        // TODO: Emit count of elements.
        if (first) {
          first = false;
        } else {
          builder.override(Style.subtle, ', ');
        }
        if (maxBreadth <= 0) {
          builder.override(Style.subtle, '…');
          break;
        }
        if (v === undefined) {
          builder.override(Style.key, String(k)).override(Style.subtle, '?');
        } else if (isFunction(v)) {
          builder.override(Style.method, String(k)).override(Style.subtle, '()');
        } else {
          builder.break();
          builder.override(Style.key, String(k)).override(Style.subtle, ': ');
          formatValue(builder, v, maxDepth - 1, maxBreadth);
        }
        maxBreadth -= 1;
      }
      builder.indent(false);
      builder.override(Style.subtle, '}');
    } else {
      // Ideally, we'd fall through to '%o', but that interacts poorly with css styles in vscode.
      // TODO: Emit count of elements.
      builder.override(Style.subtle, '{…}');
    }
  } else if (isFunction(value)) {
    builder.override(Style.method, value.name).override(Style.subtle, '()');
  } else if (typeof value === 'number') {
    builder.override(Style.number, logNumber(value, 4));
  } else if (typeof value === 'string') {
    if (value) {
      builder.override(Style.string, value);
    } else {
      builder.override(Style.subtle, "''");
    }
  } else if (['boolean', 'null', 'undefined'].includes(typeof value)) {
    builder.override(Style.keyword, String(value));
  } else {
    builder.add('%o', value);
  }
}

export function logFormatValue<T>(value: T) {
  const builder = new LogEntryBuilder({ format: 'none' });
  formatValue(builder, value);
  return builder.build()[0]; // Hack to try to merge logValue with formatValue.
}

export function formatObject(object: Objectish) {
  const builder = new LogEntryBuilder({ format: 'none' });
  const format = objectFormatters.format(builder, object);
  switch (format.result) {
    case 'builder':
      // This does ignore any non-style arguments to the format string.
      return builder.build()[0];
    case 'record':
      return format.record;
    case 'unknown':
      return undefined;
  }
}
